import React, { useState } from "react";
import moment from "moment";
import { RiLoader5Fill } from "react-icons/ri";
import cx from "classnames";

import {
  yaxisFormatterNumber,
  yaxisFormatterPercent,
  yaxisFormatter,
  tooltipLabelFormatterUnits,
  tooltipFormatterNumber,
  tooltipFormatterPercent,
  formatNumber,
  CHART_HEIGHT,
  YAXIS_WIDTH,
  COLORS,
  convertToPercents,
  LINE_COLOR,
  LINE_STROKE,
} from "../helpers";
import {
  EXPLORER_URL,
  DATE_RANGE_OPTIONS,
  DEFAULT_GROUP_PERIOD,
  SUPPORTED_TOKENS,
} from "../constants";

import VolumeChart from "../components/VolumeChart";
import FeesChart from "../components/FeesChart";
import GenericChart from "../components/GenericChart";
import DateRangeSelect from "../components/DateRangeSelect";
import SectionHeader from "../components/SectionHeader/index";
import TraderPnLChart from "../components/TraderPnLChart";
import TradersProfitLossChart from "../components/TradersProfitLossChart";

import {
  useVolumeData,
  useFeesData,
  useGlpData,
  useAumPerformanceData,
  useGlpPerformanceData,
  useTradersData,
  useFundingRateData,
  useUsersData,
  useLastSubgraphBlock,
  useLastBlock,
} from "../dataProvider";
import useChain from "../hooks/useChain";
import useDateRange from "../hooks/useDateRange";
import {
  useTotalVolume,
  useTotalFee,
  useTotalUsers,
  useTotalOpenInterest,
  useTotalValueLocked,
} from "../hooks/useTotalData";

const NOW = Math.floor(Date.now() / 1000);

export default function Stats() {
  const { chainName } = useChain();
  const [groupPeriod] = useState(DEFAULT_GROUP_PERIOD);

  const { dataRange, onDateRangeChange } = useDateRange();

  const from = dataRange.fromValue
    ? Math.floor(+new Date(dataRange.fromValue) / 1000)
    : undefined;

  const to = dataRange.toValue
    ? Math.floor(+new Date(dataRange.toValue) / 1000)
    : NOW;

  const params = { from, to, groupPeriod, chainName };

  const [fundingRateData, fundingRateLoading] = useFundingRateData(params);

  // get/calc volume
  const [volumeData, volumeLoading] = useVolumeData(params);
  const [totalVolume, totalVolumeDelta] = useTotalVolume(chainName);

  // get/calc fees
  const [feesData, feesLoading] = useFeesData(params);
  const [totalFees, totalFeesDelta] = useTotalFee(chainName);

  // get/calc klp
  const [glpData, glpLoading] = useGlpData(params);
  const [totalAum, totalAumDelta] = useTotalValueLocked(chainName);

  const [aumPerformanceData, aumPerformanceLoading] =
    useAumPerformanceData(params);

  // get/calc trading data
  const [tradersData, tradersLoading] = useTradersData(params);
  const [openInterest, openInterestDelta] = useTotalOpenInterest(chainName);

  // get/calc user
  const [usersData, usersLoading] = useUsersData(params);
  const [totalUsers, totalUsersDelta] = useTotalUsers(chainName);

  // get last block
  const [lastSubgraphBlock, , lastSubgraphBlockError] =
    useLastSubgraphBlock(chainName);
  const [lastBlock] = useLastBlock(chainName);

  const isObsolete =
    lastSubgraphBlock &&
    lastBlock &&
    lastBlock.timestamp - lastSubgraphBlock.timestamp > 3600;

  return (
    <div className="Home">
      <div className="page-title-block">
        <p className="page-description">
          Data shown is based on UTC+0.{" "}
          {!!(lastSubgraphBlock && lastBlock) && (
            <span className={cx({ warning: isObsolete })}>
              {isObsolete && "Data is obsolete. "}
              Updated {moment(lastSubgraphBlock.timestamp * 1000).fromNow()}
              &nbsp;at block{" "}
              <a
                rel="noreferrer"
                target="_blank"
                href={`${EXPLORER_URL[chainName]}block/${lastSubgraphBlock.number}`}
              >
                {lastSubgraphBlock.number}
              </a>
            </span>
          )}
          {lastSubgraphBlockError && (
            <span className="warning">
              Subgraph data is temporarily unavailable.
            </span>
          )}
        </p>
      </div>
      <div className="chart-grid">
        <div className="chart-cell stats">
          {totalVolume ? (
            <>
              <div className="total-stat-label">Total Volume</div>
              <div className="total-stat-value">
                {formatNumber(totalVolume, { currency: true })}
                {totalVolumeDelta && (
                  <span
                    className="total-stat-delta plus"
                    title="Change since previous day"
                  >
                    +
                    {formatNumber(totalVolumeDelta, {
                      currency: true,
                      compact: true,
                    })}
                  </span>
                )}
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
        <div className="chart-cell stats">
          {totalFees ? (
            <>
              <div className="total-stat-label">Total Fees</div>
              <div className="total-stat-value">
                {formatNumber(totalFees, { currency: true })}
                <span
                  className="total-stat-delta plus"
                  title="Change since previous day"
                >
                  +
                  {formatNumber(totalFeesDelta, {
                    currency: true,
                    compact: true,
                  })}
                </span>
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
        <div className="chart-cell stats">
          {totalAum ? (
            <>
              <div className="total-stat-label">KLP Pool</div>
              <div className="total-stat-value">
                {formatNumber(totalAum, { currency: true })}
                <span
                  className={cx(
                    "total-stat-delta",
                    totalAumDelta > 0 ? "plus" : "minus"
                  )}
                  title="Change since previous day"
                >
                  {totalAumDelta > 0 ? "+" : ""}
                  {formatNumber(totalAumDelta, {
                    currency: true,
                    compact: true,
                  })}
                </span>
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
        <div className="chart-cell stats">
          {totalUsers ? (
            <>
              <div className="total-stat-label">Total Users</div>
              <div className="total-stat-value">
                {formatNumber(totalUsers)}
                <span
                  className="total-stat-delta plus"
                  title="Change since previous day"
                >
                  +
                  {formatNumber(totalUsersDelta, {
                    compact: true,
                    compactDigit: 0,
                  })}
                </span>
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
        <div className="chart-cell stats">
          {openInterest !== undefined ? (
            <>
              <div className="total-stat-label">Open Interest</div>
              <div className="total-stat-value">
                {formatNumber(openInterest, { currency: true })}
                <span
                  className={cx(
                    "total-stat-delta",
                    openInterestDelta > 0 ? "plus" : "minus"
                  )}
                  title="Change since previous day"
                >
                  {openInterestDelta > 0 ? "+" : ""}
                  {formatNumber(openInterestDelta, {
                    currency: true,
                    compact: true,
                  })}
                </span>
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
      </div>
      <div className="form">
        <DateRangeSelect
          options={DATE_RANGE_OPTIONS}
          startDate={dataRange.fromValue}
          endDate={dataRange.toValue}
          onChange={onDateRangeChange}
        />
      </div>

      <SectionHeader>Market Breakdown</SectionHeader>
      <div className="chart-grid">
        <div className="chart-cell">
          <VolumeChart
            data={volumeData}
            loading={volumeLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={tradersLoading}
            title="Open Interest"
            data={tradersData?.data.map((item) => ({
              all: item.openInterest,
              ...item,
            }))}
            controls={{
              convertToPercents: convertToPercents,
            }}
            yaxisDataKey="all"
            items={[
              { key: "shortOpenInterest", name: "Short", color: COLORS[0] },
              { key: "longOpenInterest", name: "Long", color: COLORS[1] },
            ]}
          />
        </div>
      </div>

      <SectionHeader>Fee Breakdown</SectionHeader>
      <div className="chart-grid">
        <div className="chart-cell">
          <FeesChart
            data={feesData}
            loading={feesLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
          />
        </div>
      </div>

      <SectionHeader>Pool Breakdown</SectionHeader>
      <div className="chart-grid">
        <div className="chart-cell">
          <GenericChart
            loading={fundingRateLoading}
            title="Borrowing Rate Annualized"
            data={fundingRateData}
            yaxisDataKey="ETH"
            yaxisTickFormatter={yaxisFormatterPercent}
            tooltipFormatter={tooltipFormatterPercent}
            items={SUPPORTED_TOKENS[chainName].map((tokenSymbol) => ({
              key: tokenSymbol,
            }))}
            type="Line"
            yaxisDomain={[0, 90 /* ~87% is a maximum yearly borrow rate */]}
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={glpLoading}
            title="AUM & Klp Supply"
            data={glpData}
            yaxisDataKey="glpSupply"
            yaxisDomain={[0, "dataMax + 1100000"]}
            yaxisTickFormatter={yaxisFormatter}
            tooltipFormatter={tooltipFormatterNumber}
            items={[
              { key: "aum", name: "AUM" },
              { key: "glpSupply", name: "KLP Supply" },
            ]}
            type="Line"
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={aumPerformanceLoading}
            title="AUM Daily Usage"
            data={aumPerformanceData}
            yaxisDataKey="usage"
            yaxisTickFormatter={yaxisFormatterPercent}
            tooltipFormatter={tooltipFormatterPercent}
            items={[{ key: "usage", name: "Daily Usage" }]}
            description="Formula = Daily Volume / KLP Pool * 100"
            type="Line"
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={aumPerformanceLoading}
            title="AUM Performance Annualized"
            data={aumPerformanceData}
            yaxisDataKey="apr"
            yaxisTickFormatter={yaxisFormatterPercent}
            tooltipFormatter={tooltipFormatterPercent}
            items={[{ key: "apr", name: "APR" }]}
            description="Formula = Daily Fees / KLP Pool * 365 days * 100"
            type="Line"
          />
        </div>
      </div>

      <SectionHeader>Users Breakdown</SectionHeader>
      <div className="chart-grid">
        <div className="chart-cell">
          <GenericChart
            loading={usersLoading}
            title="New Users"
            data={usersData?.map((item) => ({ ...item, all: item.newCount }))}
            truncateYThreshold={6000}
            yaxisDataKey="newCount"
            rightYaxisDataKey="uniqueCountCumulative"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: "all", name: "All" },
              {
                key: "cumulativeNewUserCount",
                name: "Cumulative",
                type: "Line",
                yAxisId: "right",
                strokeWidth: LINE_STROKE,
                color: LINE_COLOR,
              },
            ]}
            type="Composed"
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={usersLoading}
            title="DAU"
            data={usersData?.map((item) => ({
              ...item,
              all: item.uniqueCount,
            }))}
            truncateYThreshold={7000}
            yaxisDataKey="uniqueCount"
            rightYaxisDataKey="all"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: "newCount", name: "New User" },
              { key: "oldCount", name: "Existing User" },
            ]}
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={usersLoading}
            title="Unique Users"
            data={usersData}
            truncateYThreshold={6500}
            yaxisDataKey="uniqueCount"
            rightYaxisDataKey="uniqueSum"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: "uniqueMintBurnCount", name: "Unique LPs" },
              {
                key: "uniqueMarginSwapCount",
                name: "Unique Traders",
              },
            ]}
          />
        </div>
      </div>

      <SectionHeader>Traders Breakdown</SectionHeader>
      <div className="chart-grid">
        <div className="chart-cell">
          <TraderPnLChart
            tradersData={tradersData}
            tradersLoading={tradersLoading}
          />
        </div>
        <div className="chart-cell">
          <TradersProfitLossChart
            syncId="tradersId"
            loading={tradersLoading}
            tradersData={tradersData}
            yaxisWidth={YAXIS_WIDTH}
            chartHeight={CHART_HEIGHT}
          />
        </div>
      </div>

      {/* TODO: revert back */}
      {/* <div className="chart-cell">
          <ChartWrapper
            title="Klp Performance"
            loading={glpLoading}
            data={glpPerformanceData}
            csvFields={[
              { key: "syntheticPrice" },
              { key: "glpPrice", name: "Klp Price" },
              { key: "glpPlusFees", name: "Klp Plus Price" },
              { key: "lpBtcPrice" },
              { key: "lpEthPrice" },
              { key: "performanceSyntheticCollectedFees" },
              { key: "indexBtcCount" },
              { key: "indexEthCount" },
              { key: "indexBnbCount" },
              { key: "indexStableCount" },
              { key: "BTC_WEIGHT" },
              { key: "ETH_WEIGHT" },
              { key: "BNB_WEIGHT" },
              { key: "STABLE_WEIGHT" },
            ]}
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <LineChart data={glpPerformanceData}>
                <CartesianGrid strokeDasharray="10 10" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={tooltipLabelFormatter}
                  minTickGap={30}
                />
                <YAxis
                  dataKey="performanceSyntheticCollectedFees"
                  domain={[80, 180]}
                  unit="%"
                  tickFormatter={yaxisFormatterNumber}
                  width={YAXIS_WIDTH}
                />
                <Tooltip
                  formatter={tooltipFormatterNumber}
                  labelFormatter={tooltipLabelFormatter}
                  contentStyle={{ textAlign: "left" }}
                />
                <Legend />
                <Line
                  dot={false}
                  isAnimationActive={false}
                  type="monotone"
                  unit="%"
                  dataKey="performanceLpBtcCollectedFees"
                  name="% LP BTC-USD (w/ fees)"
                  stroke={COLORS[2]}
                />
                <Line
                  dot={false}
                  isAnimationActive={false}
                  type="monotone"
                  unit="%"
                  dataKey="performanceLpEthCollectedFees"
                  name="% LP ETH-USD (w/ fees)"
                  stroke={COLORS[4]}
                />
                <Line
                  dot={false}
                  isAnimationActive={false}
                  type="monotone"
                  unit="%"
                  dataKey="performanceSyntheticCollectedFees"
                  name="% Index (w/ fees)"
                  stroke={COLORS[0]}
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="chart-description">
              <p>
                <span style={{ color: COLORS[0] }}>% of Index (with fees)</span>{" "}
                is Klp with fees / Index Price * 100. Index is a basket of 10%
                BTC, 20% ETH, 20% BNB and 50% BUSD rebalanced
                once&nbsp;a&nbsp;day
                <br />
                <span style={{ color: COLORS[4] }}>
                  % of LP ETH-USD (with fees)
                </span>{" "}
                is Klp Price with fees / LP ETH-USD * 100
                <br />
              </p>
            </div>
          </ChartWrapper>
        </div>
        <div className="chart-cell">
          <ChartWrapper
            title="Klp Price Comparison"
            loading={glpLoading}
            data={glpPerformanceData}
            csvFields={[
              { key: "syntheticPrice" },
              { key: "glpPrice", name: "Klp Price" },
              { key: "glpPlusFees", name: "Klp Plus Price" },
              { key: "lpBtcPrice" },
              { key: "lpEthPrice" },
              { key: "performanceSyntheticCollectedFees" },
              { key: "indexBtcCount" },
              { key: "indexEthCount" },
              { key: "indexBnbCount" },
              { key: "indexStableCount" },
              { key: "BTC_WEIGHT" },
              { key: "ETH_WEIGHT" },
              { key: "BNB_WEIGHT" },
              { key: "STABLE_WEIGHT" },
            ]}
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <LineChart data={glpPerformanceData}>
                <CartesianGrid strokeDasharray="10 10" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={tooltipLabelFormatter}
                  minTickGap={30}
                />
                <YAxis
                  dataKey="glpPrice"
                  domain={[0.4, 1.7]}
                  tickFormatter={yaxisFormatterNumber}
                  width={YAXIS_WIDTH}
                />
                <Tooltip
                  formatter={tooltipFormatterNumber}
                  labelFormatter={tooltipLabelFormatter}
                  contentStyle={{ textAlign: "left" }}
                />
                <Legend />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  unit="$"
                  strokeWidth={1}
                  dot={false}
                  dataKey="syntheticPrice"
                  name="Index Price"
                  stroke={COLORS[2]}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  unit="$"
                  strokeWidth={1}
                  dot={false}
                  dataKey="glpPrice"
                  name="Klp Price"
                  stroke={COLORS[1]}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  unit="$"
                  strokeWidth={2}
                  dot={false}
                  dataKey="glpPlusFees"
                  name="Klp w/ fees"
                  stroke={COLORS[3]}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  unit="$"
                  strokeWidth={1}
                  dot={false}
                  dataKey="lpBtcPrice"
                  name="LP BTC-USD"
                  stroke={COLORS[2]}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  unit="$"
                  strokeWidth={1}
                  dot={false}
                  dataKey="lpEthPrice"
                  name="LP ETH-USD"
                  stroke={COLORS[4]}
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="chart-description">
              <p>
                <span style={{ color: COLORS[3] }}>Klp with fees</span> is based
                on KLP share of fees received and excluding esKTX rewards
                <br />
                <span style={{ color: COLORS[2] }}>Index Price</span> is a
                basket of 10% BTC, 20% ETH, 20% BNB and 50% USD rebalanced
                once&nbsp;a&nbsp;day
              </p>
            </div>
          </ChartWrapper>
        </div> */}
    </div>
  );
}
